import { useCallback, useRef } from 'react';

import { HYPATIA_URL } from '@frontend/envConfig';
import { useStreamExecute } from '@frontend/features/agents/hooks';
import { executeSpecializedAgent } from '@frontend/features/agents/services/specialized-agent';
import type { StreamEvent } from '@frontend/features/agents/types/streaming';
import { useInstanceId } from '@frontend/hooks';

import { useStewardAgent } from './useStewardAgent';

/** Card identity the Steward names a stack from. */
export type NameSuggestCard = { id: string; title: string };

// Give the Steward this long to answer before we fall back to a placeholder.
const SUGGEST_TIMEOUT_MS = 8000;

/**
 * Builds the `name-suggest:` message. The live Steward owns a system-prompt rule
 * that, on this prefix, replies with JUST a short stack name (no prose).
 */
function buildMessage(cards: NameSuggestCard[]): string {
  const lines = cards.map(c => `- [${c.id}] ${c.title}`).join('\n');
  return `name-suggest: Suggest a short, human stack name (a few words) for these cards:\n${lines}`;
}

/** Trim the Steward reply to a usable label, or null if it's unusable. */
function sanitize(raw: string): string | null {
  const firstLine = raw.trim().split('\n')[0]?.trim() ?? '';
  const cleaned = firstLine
    .replace(/^["'`]+|["'`]+$/g, '') // surrounding quotes/backticks
    .replace(/\.+$/, '') // trailing period
    .trim();
  return cleaned ? cleaned.slice(0, 60) : null;
}

/**
 * One-shot Steward call that suggests a name for a freshly-formed stack. Uses
 * the same no-param two-step launch as the ask bar (init `execute {}` →
 * memory_id, then a `name-suggest:` message over SSE), but with its OWN
 * ephemeral thread so it never touches the ask bar's conversation. Resolves to a
 * sanitised name, or `null` on error / timeout / if the Steward isn't installed —
 * the caller falls back to a placeholder.
 */
export function useSuggestStackName() {
  const { instanceId } = useInstanceId();
  const { agentUuid } = useStewardAgent();

  const textRef = useRef('');
  const resolveRef = useRef<((name: string | null) => void) | null>(null);

  const finish = useCallback((name: string | null) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.(name);
  }, []);

  const { execute } = useStreamExecute({
    onChunk: (event: StreamEvent) => {
      if (event.event_type === 'response_chunk') {
        textRef.current += (event.data.content as string) ?? '';
      }
    },
    onComplete: () => finish(sanitize(textRef.current)),
    onError: () => finish(null),
  });

  const suggest = useCallback(
    async (cards: NameSuggestCard[]): Promise<string | null> => {
      if (!agentUuid || !instanceId || cards.length === 0) return null;
      textRef.current = '';

      // Turn 1 — open a fresh ephemeral thread.
      let memoryId: string | undefined;
      let etag: string | undefined;
      try {
        const execUrl = `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentUuid}/execute`;
        const { data, etag: initEtag } = await executeSpecializedAgent(execUrl, {}, false)();
        memoryId = data.memory_id;
        etag = initEtag ?? data.etag ?? undefined;
      } catch {
        return null;
      }
      if (!memoryId) return null;

      // Turn 2 — ask for the name and resolve on completion (or time out).
      return new Promise<string | null>(resolve => {
        resolveRef.current = resolve;
        const timer = setTimeout(() => finish(null), SUGGEST_TIMEOUT_MS);
        const settle = (name: string | null) => {
          clearTimeout(timer);
          resolve(name);
        };
        resolveRef.current = settle;
        execute({
          instanceId,
          agentId: agentUuid,
          parameters: {},
          messages: [{ text: buildMessage(cards) }],
          memoryId,
          etag,
        });
      });
    },
    [agentUuid, instanceId, execute, finish]
  );

  return { suggest };
}
